/**
 * shared/pmove/jump.ts — PM_CheckJump / Jump_Start / land slowdown / jump step.
 *
 * OWNER: PMOVE agent.
 * T6: PM_CheckJump 0x607680, Jump_Start 0x93e680, PM_JumpLandSlowdown 0x454330, PM_GetJumpLandScale
 * 0x6350b0, PM_CheckJumpStep 0x4ff870 [PM §3]. Structure and the ladder push-off follow
 * [T5 bg_jump.cpp], whose constants all match the T6 decompile.
 */
import {
  ENTITYNUM_NONE, PM_JUMP_COOLDOWN_MS, PM_LAND_FRICTION_MAX, PM_LAND_SCALE, PM_LAND_SCALE_HIGHER,
  PM_LAND_TIME_HIGHER_MS, PM_LAND_TIME_MS,
} from '../constants';
import { SURF } from '../collision/types';
import { type ReadonlyVec3, type Vec3, vnormalize } from '../math';
import { Button, PEv, PMF, PmType, type PlayerState } from '../types';
import { effectiveStance, PM_STANCE_STAND, type Pm } from './pm';
import { groundSurfaceType } from './footsteps';

/** Jump_ClearState [T5 bg_jump.cpp]. */
export function jumpClearState(ps: PlayerState): void {
  ps.pm_flags &= ~PMF.JUMPING;
  ps.jumpOriginZ = 0;
}

/** Jump_ActivateSlowdown [T5 bg_jump.cpp]: going prone counts as a "landing" (PM_CheckDuck). */
export function jumpActivateSlowdown(ps: PlayerState): void {
  if (!ps.pm_time) {
    ps.pm_flags |= PMF.JUMPING;
    ps.pm_time = PM_LAND_TIME_MS;
  }
}

/**
 * PM_GetJumpLandScale (0x6350b0) = T5 Jump_GetLandFactor / Jump_GetSlowdownFriction:
 * 1 + pm_time * 1.5 / 1700, 2.5 once pm_time >= 1700 [PM §3; 2.5 @0xbf76bc, 1/1700 @0xc00b9c].
 */
export function jumpLandFactor(pm: Pm): number {
  if (!pm.ctx.dvars.jumpSlowdownEnable) return 1;
  const t = pm.ps.pm_time;
  if (t < 1700) return t * 1.5 * 0.00058823527 + 1;
  return PM_LAND_FRICTION_MAX;
}

/** Friction multiplier while PMF_JUMPING (T5 Jump_ReduceFriction). */
export function jumpReduceFriction(pm: Pm): number {
  if (pm.ps.pm_time > PM_LAND_TIME_MS) {
    jumpClearState(pm.ps);
    return 1;
  }
  return jumpLandFactor(pm);
}

/**
 * PM_JumpLandSlowdown (0x454330) / T5 Jump_ApplySlowdown: top of WalkMove while PMF_JUMPING.
 * First ground frame after a jump: velocity x0.65 and pm_time 1800, or x0.5 and 1200 if the
 * player landed >= 18 u above the take-off point [PM §3].
 */
export function jumpApplySlowdown(pm: Pm): void {
  const ps = pm.ps;
  let scale = 1;
  if (ps.pm_time <= PM_LAND_TIME_MS) {
    if (!ps.pm_time) {
      if (ps.jumpOriginZ + 18 <= ps.origin[2]) { // 18 @0xc48898
        ps.pm_time = PM_LAND_TIME_HIGHER_MS;
        scale = PM_LAND_SCALE_HIGHER;
      } else {
        ps.pm_time = PM_LAND_TIME_MS;
        scale = PM_LAND_SCALE;
      }
    }
  } else {
    jumpClearState(ps);
    scale = PM_LAND_SCALE;
  }
  if (pm.ctx.dvars.jumpSlowdownEnable) {
    ps.velocity[0] *= scale; ps.velocity[1] *= scale; ps.velocity[2] *= scale;
  }
}

/**
 * PM_CheckJump (0x607680) [PM §3]. Returns true if a jump started (WalkMove then runs AirMove).
 * Uses ps.oldButtons for the re-press rule (ARCHITECTURE §9.2): holding jump never re-jumps.
 */
export function checkJump(pm: Pm): boolean {
  const ps = pm.ps;
  if (ps.pm_flags & PMF.NO_JUMP) return false;
  if (pm.cmd.serverTime - ps.jumpTime < PM_JUMP_COOLDOWN_MS) return false; // "499 <": 500 ms
  if (ps.pm_flags & (PMF.FROZEN | PMF.MANTLE)) return false;
  if (ps.pm_type >= PmType.FREEZE) return false;
  if (effectiveStance(ps) !== PM_STANCE_STAND && ps.groundEntityNum !== ENTITYNUM_NONE) return false; // stand only
  if (!(pm.cmd.buttons & Button.JUMP)) return false;
  if (pm.oldButtons & Button.JUMP) { // must re-press
    pm.cmd.buttons &= ~Button.JUMP;
    return false;
  }
  if (pm.groundTrace.surfaceFlags & SURF.SLICK) return false;
  jumpStart(pm, pm.ctx.dvars.jumpHeight); // 39 @0xc31314
  // Jump_AddSurfaceEvent: ladder jumps use surface 21 (wood) [T5 bg_jump.cpp]
  pm.ctx.addEvent(ps, PEv.JUMP, ps.pm_flags & PMF.LADDER ? 21 : groundSurfaceType(pm));
  if (ps.pm_flags & PMF.LADDER) jumpPushOffLadder(pm);
  return true;
}

/**
 * Jump_Start (0x93e680) [PM §3]: v.z = sqrt(2*g*h) (249.8 for g=800, h=39), divided by the land
 * factor when jumping again inside the 1.8 s land window.
 */
export function jumpStart(pm: Pm, height: number): void {
  const ps = pm.ps;
  let velocitySqrd = height * 2 * ps.gravity;
  if ((ps.pm_flags & PMF.JUMPING) && ps.pm_time <= PM_LAND_TIME_MS) {
    const factor = jumpLandFactor(pm);
    if (factor !== 0) velocitySqrd /= factor;
  }
  pm.groundPlane = false;
  pm.almostGroundPlane = false;
  pm.walking = false;
  ps.groundEntityNum = ENTITYNUM_NONE;
  ps.jumpTime = pm.cmd.serverTime;
  ps.jumpOriginZ = ps.origin[2];
  ps.velocity[2] = Math.sqrt(velocitySqrd);
  ps.pm_flags &= ~(PMF.TIME_SLOWDOWN | PMF.TIME_KNOCKBACK);
  ps.pm_flags |= PMF.JUMPING;
  ps.pm_time = 0;
  ps.sprintButtonUpRequired = 0;
  ps.aimSpreadScale = Math.min(255, ps.aimSpreadScale + pm.ctx.dvars.jumpSpreadAdd); // jump_spreadAdd 64
}

const flatForward: Vec3 = [0, 0, 0];
const pushOffDir: Vec3 = [0, 0, 0];
/** Jump_PushOffLadder [T5 bg_jump.cpp:261; PM §3 Ladder]: v.z *= 0.75, horizontal 128 away from the ladder. */
export function jumpPushOffLadder(pm: Pm): void {
  const ps = pm.ps;
  ps.velocity[2] *= 0.75;
  flatForward[0] = pm.forward[0]; flatForward[1] = pm.forward[1]; flatForward[2] = 0;
  vnormalize(flatForward);
  const lv = ps.vLadderVec;
  let dot = lv[0] * pm.forward[0] + lv[1] * pm.forward[1] + lv[2] * pm.forward[2];
  if (dot >= 0) {
    pushOffDir[0] = flatForward[0]; pushOffDir[1] = flatForward[1]; pushOffDir[2] = flatForward[2];
  } else {
    dot = flatForward[0] * lv[0] + flatForward[1] * lv[1] + flatForward[2] * lv[2];
    pushOffDir[0] = flatForward[0] - 2 * dot * lv[0];
    pushOffDir[1] = flatForward[1] - 2 * dot * lv[1];
    pushOffDir[2] = flatForward[2] - 2 * dot * lv[2];
    vnormalize(pushOffDir);
  }
  const v = pm.ctx.dvars.jumpLadderPushVel; // 128
  ps.velocity[0] = v * pushOffDir[0];
  ps.velocity[1] = v * pushOffDir[1];
  ps.pm_flags &= ~PMF.LADDER;
}

/**
 * PM_CheckJumpStep (0x4ff870) / T5 Jump_GetStepHeight: while JUMPING and below the jump apex,
 * the allowed step is min(jump_stepSize 18, jumpOriginZ + 39 - origin.z).
 */
export function jumpGetStepHeight(pm: Pm, origin: ReadonlyVec3, out: { value: number }): boolean {
  const ps = pm.ps;
  const top = ps.jumpOriginZ + pm.ctx.dvars.jumpHeight;
  if (top <= origin[2]) return false;
  out.value = pm.ctx.dvars.jumpStepSize;
  if (origin[2] + out.value > top) out.value = top - origin[2];
  return true;
}

/** Jump_IsPlayerAboveMax [T5 bg_jump.cpp]. */
export function jumpIsPlayerAboveMax(pm: Pm): boolean {
  return pm.ps.origin[2] >= pm.ps.jumpOriginZ + pm.ctx.dvars.jumpHeight;
}

/** Jump_ClampVelocity [T5 bg_jump.cpp]: after a jump-step, never rise above the jump apex. */
export function jumpClampVelocity(pm: Pm, origin: ReadonlyVec3): void {
  const ps = pm.ps;
  if (ps.origin[2] - origin[2] <= 0) return;
  const heightDiff = ps.jumpOriginZ + pm.ctx.dvars.jumpHeight - ps.origin[2];
  if (heightDiff >= 0.1) {
    const maxJumpVel = Math.sqrt(heightDiff * 2 * ps.gravity);
    if (ps.velocity[2] > maxJumpVel) ps.velocity[2] = maxJumpVel;
  } else {
    ps.velocity[2] = 0;
  }
}
